import { Link } from 'react-router-dom';
import { ArrowLeft, Edit, Store, Loader2 } from 'lucide-react';

import { Button } from '@/components/atoms/button';
import { Badge } from '@/components/atoms/badge';
import { Switch } from '@/components/atoms/switch';
import { StatusBadge } from '@/components/molecules/status-badge';
import { DeleteConfirmDialog } from '@/components/molecules/delete-confirm-dialog';
import type { Product } from '@/lib/types';

interface ProductDetailHeaderProps {
  product: Product;
  onToggleStatus: () => void;
  onDelete: () => void;
  isUpdatingStatus: boolean;
  isDeleting: boolean;
  translateCategory: (name: string) => string;
}

export function ProductDetailHeader({
  product,
  onToggleStatus,
  onDelete,
  isUpdatingStatus,
  isDeleting,
  translateCategory,
}: ProductDetailHeaderProps) {
  const productRefId = product.refId || product.id;

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-3">
        <Link to="/products">
          <Button variant="ghost" size="icon" className="shrink-0">
            <ArrowLeft className="size-5" />
          </Button>
        </Link>
        <div className="min-w-0">
          <h1 className="text-xl md:text-2xl font-bold truncate">{product.name}</h1>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {Array.isArray(product.categories) && product.categories.length > 0 && (
              <>
                {product.categories.slice(0, 2).map((cat) => (
                  <Badge key={cat.id} variant="outline" className="text-xs">
                    {translateCategory(cat.name)}
                  </Badge>
                ))}
              </>
            )}
            {(!Array.isArray(product.categories) || !product.categories.length) && product.categoryName && (
              <span className="text-sm text-muted-foreground">{translateCategory(product.categoryName)}</span>
            )}
            {(product.shopName || product.shop?.name) && (
              <Link
                to={`/shops/${product.shop?.refId || product.shop?.id || product.shopId}`}
                className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1"
              >
                <Store className="size-3" />
                {product.shopName || product.shop?.name}
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2 border rounded-lg px-2 py-1">
          {isUpdatingStatus && (
            <Loader2 className="size-4 animate-spin text-muted-foreground" />
          )}
          <Switch
            checked={product.status === 'active'}
            onCheckedChange={onToggleStatus}
            disabled={isUpdatingStatus}
          />
        </div>
        <StatusBadge status={product.status} />
        <Link to={`/products/${productRefId}/edit`}>
          <Button variant="outline" size="sm">
            <Edit className="size-4" />
          </Button>
        </Link>
        <DeleteConfirmDialog
          itemName={product.name}
          onConfirm={onDelete}
          isLoading={isDeleting}
          variant="icon"
        />
      </div>
    </div>
  );
}
